'use client'
import React, { useEffect, useRef, useState, Suspense } from 'react'

import { useRouter } from 'next/navigation'

import _isEmpty from 'lodash/isEmpty'

import GlammaticLogo from '@/components/modules/glammatic/components/GlammaticLogo'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CenteredLoader } from '@/components/ui/loading'
import { ErrorMessage } from '@/components/ui/message'
import { FullScreen } from '@/components/ui/scroll'
import useAsyncEffect from '@/hooks/use-async-effect'
import { useGlammaticApi } from '@/hooks/use-glammatic-api'
import useQueryParams from '@/hooks/use-query-params'
import useStorage from '@/hooks/use-storage'
import { USER_AUTH_STORAGE_KEY } from '@/hooks/use-storage/use-storage.constants'
import DateUtils from '@/lib/date/date'
import ErrorHandler from '@/lib/error/handling'
import { trackLoginSuccess } from '@/lib/google/analytics'
import { isUserSessionValid } from '@/lib/user/auth'
import { createOrUpdateUser, registerUserLogin } from '@/server/actions/user/create'

import { BUSINESS_SELECTION_SLUG } from '../constants'

import LoginRedirectOptions from './components/LoginRedirectOptions'
import {
  AUTH_CALLBACK_ERROR_DESCRIPTION_PARAM,
  AUTH_CALLBACK_ERROR_PARAM,
} from './page.constants'
import { clearAuthCallbackErrorParams, getAuthCallbackErrorMessage } from './page.utils'

function LoginContent() {
  const router = useRouter()

  const { me } = useGlammaticApi()
  const authCallbackErrorParam = useQueryParams(AUTH_CALLBACK_ERROR_PARAM)
  const authCallbackErrorDescriptionParam = useQueryParams(
    AUTH_CALLBACK_ERROR_DESCRIPTION_PARAM
  )

  const { setValue: setUser, storageLoaded } = useStorage(USER_AUTH_STORAGE_KEY)

  const skipSilentLoginRef = useRef(false)

  const [error, setError] = useState<string | null>('')
  const [loading, setLoading] = useState(true)

  async function processSuccessfulLogin(response: UserAuth) {
    if (!isUserSessionValid(response)) {
      return
    }

    const { expirationTime, refreshTokenExpirationTime } = response
    const expiryRaw = expirationTime || refreshTokenExpirationTime
    const storageOptions = DateUtils.isValid(expiryRaw)
      ? { expiry: DateUtils.parse(expiryRaw) }
      : {}

    setUser(response, storageOptions)

    try {
      await createOrUpdateUser({ redirectToLogin: false, clearAuthCookies: false })
      const userAfterLogin = await registerUserLogin({
        redirectToLogin: false,
        clearAuthCookies: false,
      })
      const authWithLoginCount: UserAuth = {
        ...response,
        user: {
          ...response.user,
          loginCount: userAfterLogin.loginCount,
          lastLoginAt: userAfterLogin.lastLoginAt,
        },
      }
      setUser(authWithLoginCount, storageOptions)
    } catch (loginError) {
      ErrorHandler.handle(loginError as Error)
    }

    trackLoginSuccess(response)

    router.push(BUSINESS_SELECTION_SLUG)
  }

  useEffect(() => {
    const authCallbackErrorMessage = getAuthCallbackErrorMessage(
      authCallbackErrorParam.get(),
      authCallbackErrorDescriptionParam.get()
    )
    if (!authCallbackErrorMessage) {
      return
    }

    skipSilentLoginRef.current = true
    setError(authCallbackErrorMessage)
    setLoading(false)
    clearAuthCallbackErrorParams()
  }, [])

  useAsyncEffect(async () => {
    if (!storageLoaded) {
      return
    }

    if (skipSilentLoginRef.current) {
      return
    }

    let logged = false
    try {
      setLoading(true)
      const response = await me({ forceDataRefresh: true }, { retries: 0 })
      if (!_isEmpty(response) && isUserSessionValid(response)) {
        await processSuccessfulLogin(response)
        logged = true
      }
    } catch {
      // pass
    } finally {
      if (!logged) {
        setLoading(false)
      }
    }
  }, [storageLoaded])

  return (
    <FullScreen>
      <div className="w-full max-w-md flex-shrink-0">
        <Card>
          <CardHeader className="space-y-4">
            <div className="flex justify-center mb-4">
              <GlammaticLogo width={256} priority />
            </div>
            <div className="text-center space-y-2">
              <CardTitle className="text-2xl font-bold">Your Growth Hub</CardTitle>
              <p className="text-placeholder text-sm">
                Everything you need to manage, optimize, and grow — all in one place.
              </p>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading && <CenteredLoader minHeight="10vh" message="" />}
            {!loading && <LoginRedirectOptions />}
            {error && <ErrorMessage className="mt-4" message={error} />}
          </CardContent>
        </Card>
      </div>
    </FullScreen>
  )
}

function Login() {
  return (
    <Suspense fallback={<CenteredLoader />}>
      <LoginContent />
    </Suspense>
  )
}

export default Login
